import React, { useCallback, useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, RefreshCw, Settings2, LogIn, LogOut, BedDouble, X } from "lucide-react";
import { StaffPinGate } from "../components/StaffPinGate";
import { fetchCheckinDay, hasCheckinAuth, saveCheckinSettings, PIN_KEY } from "../lib/checkinApi";
import { WaOpenModeBox } from "../components/WaOpenModeBox";
import { GuestCard } from "../components/checkin/GuestCard";
import { ShowGuestModal } from "../components/checkin/ShowGuestModal";
import { GuestQuestionsWidget } from "../components/checkin/GuestQuestionsWidget";

const iso = (d) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 10); };
const shift = (day, n) => { const d = new Date(day + "T12:00:00"); d.setDate(d.getDate() + n); return iso(d); };
const longDate = (day) => new Date(day + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

export default function CheckinPage() {
  const [authed, setAuthed] = useState(hasCheckinAuth());
  const [day, setDay] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try { setData(await fetchCheckinDay(day)); }
    catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem(PIN_KEY); setAuthed(false); }
      else toast.error("Errore caricamento");
    } finally { setLoading(false); }
  }, [day]);

  useEffect(() => { if (authed) load(); }, [authed, load]);
  useEffect(() => {
    if (!authed) return undefined;
    const t = setInterval(() => { if (document.visibilityState === "visible") load(true); }, 60000);
    return () => clearInterval(t);
  }, [authed, load]);

  if (!authed) return <StaffPinGate title="Postazione Check-in" onUnlock={() => setAuthed(true)} />;

  const today = iso(new Date());
  const c = data?.counts || {};
  return (
    <div className="min-h-screen bg-[#f7ebdf] text-[#2b1410]" data-testid="checkin-page">
      <header className="sticky top-0 z-30 bg-[#4a1c17] text-[#f7ebdf] shadow-md pt-[env(safe-area-inset-top)]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src="/icons/icon-checkin-192.png" alt="" className="h-10 w-10 rounded-xl bg-white object-cover" />
          <div className="min-w-0">
            <h1 className="font-serif text-lg sm:text-xl font-bold leading-tight">Check-in</h1>
            <p className="text-[11px] text-[#f7ebdf]/70 truncate">Il Pozzo dei Giganti · postazione cassa</p>
          </div>
          <div className="flex-1" />
          <button onClick={() => load()} className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" title="Aggiorna" data-testid="checkin-refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
          <button onClick={() => setSettingsOpen(true)} className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" title="Wi-Fi e colazione" data-testid="checkin-settings-btn"><Settings2 className="h-4 w-4" /></button>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 pb-16">
        <div className="flex flex-wrap items-center gap-2 py-4">
          <button onClick={() => setDay(shift(day, -1))} className="h-11 w-11 rounded-full bg-white border border-[#e8dcc8] flex items-center justify-center" data-testid="checkin-prev-day"><ChevronLeft className="h-5 w-5" /></button>
          <button onClick={() => setDay(today)} className={`h-11 rounded-full px-4 text-sm font-bold border ${day === today ? "bg-[#4a1c17] text-white border-[#4a1c17]" : "bg-white border-[#e8dcc8]"}`} data-testid="checkin-today">Oggi</button>
          <button onClick={() => setDay(shift(today, 1))} className={`h-11 rounded-full px-4 text-sm font-bold border ${day === shift(today, 1) ? "bg-[#4a1c17] text-white border-[#4a1c17]" : "bg-white border-[#e8dcc8]"}`} data-testid="checkin-tomorrow">Domani</button>
          <button onClick={() => setDay(shift(day, 1))} className="h-11 w-11 rounded-full bg-white border border-[#e8dcc8] flex items-center justify-center" data-testid="checkin-next-day"><ChevronRight className="h-5 w-5" /></button>
          <h2 className="ml-2 text-base md:text-lg font-bold capitalize" data-testid="checkin-day-label">{longDate(day)}</h2>
          <div className="flex-1" />
          <div className="flex gap-2 text-xs font-bold">
            <Stat icon={LogIn} label="Arrivi" value={c.arrivi} tone="bg-[#C48B31] text-white" />
            <Stat icon={LogOut} label="Partenze" value={c.partenze} tone="bg-white border border-[#e8dcc8]" />
            <Stat icon={BedDouble} label="In casa" value={c.in_casa} tone="bg-white border border-[#e8dcc8]" />
          </div>
        </div>

        {!data && loading && <div className="py-20 text-center text-[#7c5a4e]"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>}

        {data && (
          <div className="space-y-6 lg:space-y-4">
            <GuestQuestionsWidget />
            <div className="space-y-8 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-4 lg:items-start" data-testid="checkin-columns">
            <Section title="Arrivi" subtitle="Genera il codice o invialo su WhatsApp" count={data.arrivals.length} empty="Nessun arrivo previsto" testId="checkin-arrivals">
              {data.arrivals.map((it) => <GuestCard key={it.id} item={it} kind="arrival" onChanged={() => load(true)} onShow={setShow} />)}
            </Section>
            <Section title="Partenze" subtitle="Conto frigobar e check-out" count={data.departures.length} empty="Nessuna partenza" testId="checkin-departures">
              {data.departures.map((it) => <GuestCard key={it.id} item={it} kind="departure" onChanged={() => load(true)} onShow={setShow} />)}
            </Section>
            <Section title="In casa" subtitle="Ospiti già arrivati" count={data.in_house.length} empty="Nessun ospite in casa" testId="checkin-inhouse">
              {data.in_house.map((it) => <GuestCard key={it.id} item={it} kind="inhouse" onChanged={() => load(true)} onShow={setShow} />)}
            </Section>
          </div>
            </div>
        )}
      </main>

      <ShowGuestModal item={show} settings={data?.settings} onClose={() => setShow(null)} />
      {settingsOpen && <SettingsSheet settings={data?.settings} onClose={() => setSettingsOpen(false)} onSaved={() => load(true)} />}
      <Toaster position="top-center" richColors />
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  return <div className={`h-11 rounded-full px-3 inline-flex items-center gap-1.5 ${tone}`}><Icon className="h-3.5 w-3.5" /> {label} <span className="text-base">{value ?? "–"}</span></div>;
}

function Section({ title, subtitle, count, empty, children, testId }) {
  return (
    <section data-testid={testId}>
      <div className="flex items-end gap-3 mb-3 min-w-0 h-10">
        <h2 className="font-serif text-2xl font-bold shrink-0">{title}</h2>
        <span className="text-sm text-[#7c5a4e] pb-1 truncate min-w-0 lg:hidden xl:inline" title={subtitle}>{subtitle}</span>
        <span className="ml-auto shrink-0 rounded-full bg-white border border-[#e8dcc8] px-3 py-0.5 text-sm font-bold">{count}</span>
      </div>
      {count === 0 ? <div className="rounded-3xl border border-dashed border-[#e8dcc8] bg-white/60 py-8 text-center text-sm italic text-[#7c5a4e]">{empty}</div>
        : <div className="grid grid-cols-1 gap-4">{children}</div>}
    </section>
  );
}

function SettingsSheet({ settings, onClose, onSaved }) {
  const [form, setForm] = useState({ wifi_password: settings?.wifi_password || "", breakfast_text: settings?.breakfast_text || "", welcome_note: settings?.welcome_note || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await saveCheckinSettings(form); toast.success("Salvato"); onSaved(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const field = (k, label, ph) => (
    <label className="block text-sm font-semibold">{label}
      <input value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} placeholder={ph} className="mt-1 w-full h-12 rounded-2xl border border-[#e8dcc8] bg-white px-4 text-base font-normal" data-testid={`checkin-settings-${k}`} />
    </label>
  );
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl bg-[#f7ebdf] p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="checkin-settings-sheet">
        <div className="flex items-center justify-between"><h2 className="font-serif text-xl font-bold">Dati per l'ospite</h2><button onClick={onClose} className="h-9 w-9 rounded-full bg-white flex items-center justify-center"><X className="h-4 w-4" /></button></div>
        {field("wifi_password", "Password Wi-Fi", "es. 12Giugno")}
        {field("breakfast_text", "Orario colazione", "es. 8:00 – 10:00")}
        {field("welcome_note", "Nota di benvenuto (opzionale)", "es. La Spa è aperta dalle 16 alle 20")}
        <WaOpenModeBox />
        <button onClick={save} disabled={busy} className="w-full h-12 rounded-2xl bg-[#4a1c17] text-white font-bold disabled:opacity-50" data-testid="checkin-settings-save">{busy ? "Salvo…" : "Salva"}</button>
      </div>
    </div>
  );
}
